import { useFormik } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import {
  useCreateTenantMutation,
  useUpdateTenantMutation,
} from "../../store/services/tenants";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { CreateTenant, UpdateTenant } from "../../models/users";
import { InputTextarea } from "primereact/inputtextarea";

const getValidationSchema = (isEdit: boolean) => {
  return Yup.object().shape({
    id: isEdit ? Yup.string().required('ID es requerido') : Yup.string().notRequired(),
    provider: Yup.string().required('Provider es requerido'),
    theme: Yup.string().required('Theme es requerido'),
    logo: Yup.string().url('Debe ser una URL válida').required('Logo es requerido'),
    alternateLogo: Yup.string().url('Debe ser una URL válida').required('Logo Alternativo es requerido'),
    profileLogo: Yup.string().url('Debe ser una URL válida').required('Logo de Perfil es requerido'),
    emailDomain: Yup.string().required('Dominio de Email es requerido'),
    showMainBanner: Yup.boolean().required(),
    defaultShowEventMap: Yup.boolean().required(),
  });
};

interface TenantFormData {
  id: string;
  provider: string;
  theme: string;
  logo: string;
  alternateLogo: string;
  profileLogo: string;
  emailDomain: string;
  showMainBanner: boolean;
  defaultShowEventMap: boolean;
}

interface TenantFormProps {
  initialValues?: TenantFormData;
  isEdit?: boolean;
}

export const TenantForm = ({ initialValues, isEdit = false }: TenantFormProps) => {
  const [createTenant, { isLoading: isLoadingCreate }] = useCreateTenantMutation();
  const [updateTenant, { isLoading: isLoadingUpdate }] = useUpdateTenantMutation();
  const navigate = useNavigate();
  const { tenantId } = useParams();

  const goBack = () => navigate(-1);

  const { handleSubmit, touched, errors, values, handleBlur, handleChange } =
    useFormik({
      initialValues: {
        id: initialValues?.id || "",
        provider: initialValues?.provider || "",
        theme: initialValues?.theme || "",
        logo: initialValues?.logo || "",
        alternateLogo: initialValues?.alternateLogo || "",
        profileLogo: initialValues?.profileLogo || "",
        emailDomain: initialValues?.emailDomain || "",
        showMainBanner: initialValues?.showMainBanner || false,
        defaultShowEventMap: initialValues?.defaultShowEventMap || false,
      } as TenantFormData,
      validationSchema: getValidationSchema(isEdit),
      onSubmit: (values) => {
        const { provider, theme, logo, alternateLogo, profileLogo, emailDomain, showMainBanner, defaultShowEventMap } = values;
        
        if (isEdit && tenantId) {
          // Update tenant
          const updateData: UpdateTenant = {
            id: tenantId,
            provider,
            theme,
            logo,
            alternateLogo,
            profileLogo,
            emailDomain,
            showMainBanner,
            defaultShowEventMap,
          };
          
          updateTenant(updateData)
            .unwrap()
            .then(() => {
              toast.success("Tenant actualizado exitosamente!");
              goBack();
            })
            .catch((error) => {
              const errorMessage = error?.data?.err || "No se pudo actualizar el tenant";
              toast.error(errorMessage);
            });
        } else {
          // Create tenant - No enviamos el ID, el backend lo genera automáticamente
          const createData: CreateTenant = {
            provider,
            theme,
            logo,
            alternateLogo,
            profileLogo,
            emailDomain,
            showMainBanner,
            defaultShowEventMap,
          };
          
          createTenant(createData)
            .unwrap()
            .then(() => {
              toast.success("Tenant creado exitosamente!");
              goBack();
            })
            .catch((error) => {
              const errorMessage = error?.data?.err || "No se pudo crear el tenant";
              toast.error(errorMessage);
            });
        }
      },
    });

  // Logo preview component
  const LogoPreview = ({ logoUrl, label }: { logoUrl: string; label: string }) => (
    logoUrl ? (
      <div className="mt-3 p-3 border-1 surface-border border-round">
        <small className="text-600 block mb-2">Vista previa {label}:</small>
        <img 
          src={logoUrl} 
          alt={`Preview ${label}`} 
          className="border-round shadow-1"
          style={{ width: '80px', height: '60px', objectFit: 'contain' }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
            const errorElement = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
            if (errorElement) {
              errorElement.textContent = 'Error al cargar imagen';
              errorElement.style.display = 'block';
            }
          }}
        />
        <small className="text-red-500" style={{ display: 'none' }}></small>
      </div>
    ) : null
  );

  return (
    <div className="surface-card p-4 shadow-2 border-round">
      <form onSubmit={handleSubmit} className="formgrid grid">
        {isEdit && (
          <div className="field col-12 md:col-6">
            <label htmlFor="id" className="font-medium text-900">ID del Tenant *</label>
            <InputText
              id="id"
              name="id"
              value={values.id}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isEdit || isLoadingCreate || isLoadingUpdate}
              className={`w-full mt-2 ${touched.id && errors.id ? 'p-invalid' : ''}`}
              placeholder="Ingrese el ID del tenant"
            />
            {touched.id && errors.id && (
              <small className="p-error block mt-1">{errors.id}</small>
            )}
            <small className="text-600 block mt-1">El ID no se puede modificar</small>
          </div>
        )}

        <div className={`field col-12 ${isEdit ? 'md:col-6' : 'md:col-6'}`}>
          <label htmlFor="provider" className="font-medium text-900">Provider *</label>
          <InputText
            id="provider"
            name="provider"
            value={values.provider}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isLoadingCreate || isLoadingUpdate}
            className={`w-full mt-2 ${touched.provider && errors.provider ? 'p-invalid' : ''}`}
            placeholder="Nombre del proveedor"
          />
          {touched.provider && errors.provider && (
            <small className="p-error block mt-1">{errors.provider}</small>
          )}
        </div>

        <div className="field col-12">
          <label htmlFor="theme" className="font-medium text-900">Theme *</label>
          <InputTextarea
            id="theme"
            name="theme"
            value={values.theme}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isLoadingCreate || isLoadingUpdate}
            className={`w-full mt-2 ${touched.theme && errors.theme ? 'p-invalid' : ''}`}
            rows={4}
            placeholder="Configuración del tema en formato JSON"
          />
          {touched.theme && errors.theme && (
            <small className="p-error block mt-1">{errors.theme}</small>
          )}
        </div>

        <div className="field col-12 md:col-4">
          <label htmlFor="logo" className="font-medium text-900">Logo Principal *</label>
          <InputText
            id="logo"
            name="logo"
            value={values.logo}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isLoadingCreate || isLoadingUpdate}
            className={`w-full mt-2 ${touched.logo && errors.logo ? 'p-invalid' : ''}`}
            placeholder="https://example.com/logo.png"
          />
          {touched.logo && errors.logo && (
            <small className="p-error block mt-1">{errors.logo}</small>
          )}
          <LogoPreview logoUrl={values.logo} label="Logo Principal" />
        </div>

        <div className="field col-12 md:col-4">
          <label htmlFor="alternateLogo" className="font-medium text-900">Logo Alternativo *</label>
          <InputText
            id="alternateLogo"
            name="alternateLogo"
            value={values.alternateLogo}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isLoadingCreate || isLoadingUpdate}
            className={`w-full mt-2 ${touched.alternateLogo && errors.alternateLogo ? 'p-invalid' : ''}`}
            placeholder="https://example.com/alternate-logo.png"
          />
          {touched.alternateLogo && errors.alternateLogo && (
            <small className="p-error block mt-1">{errors.alternateLogo}</small>
          )}
          <LogoPreview logoUrl={values.alternateLogo} label="Logo Alternativo" />
        </div>

        <div className="field col-12 md:col-4">
          <label htmlFor="profileLogo" className="font-medium text-900">Logo de Perfil *</label>
          <InputText
            id="profileLogo"
            name="profileLogo"
            value={values.profileLogo}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isLoadingCreate || isLoadingUpdate}
            className={`w-full mt-2 ${touched.profileLogo && errors.profileLogo ? 'p-invalid' : ''}`}
            placeholder="https://example.com/profile-logo.png"
          />
          {touched.profileLogo && errors.profileLogo && (
            <small className="p-error block mt-1">{errors.profileLogo}</small>
          )}
          <LogoPreview logoUrl={values.profileLogo} label="Logo de Perfil" />
        </div>

        <div className="field col-12 md:col-6">
          <label htmlFor="emailDomain" className="font-medium text-900">Dominio de Email *</label>
          <InputText
            id="emailDomain"
            name="emailDomain"
            value={values.emailDomain}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isLoadingCreate || isLoadingUpdate}
            className={`w-full mt-2 ${touched.emailDomain && errors.emailDomain ? 'p-invalid' : ''}`}
            placeholder="example.com"
          />
          {touched.emailDomain && errors.emailDomain && (
            <small className="p-error block mt-1">{errors.emailDomain}</small>
          )}
          <small className="text-600 block mt-1">
            Dominio de email asociado al tenant (opcional)
          </small>
        </div>

        <div className="field col-12 md:col-6">
          <div className="flex flex-column gap-3">
            <div className="flex align-items-center">
              <Checkbox
                id="showMainBanner"
                name="showMainBanner"
                checked={values.showMainBanner}
                onChange={(e) => handleChange({ target: { name: 'showMainBanner', value: e.checked } })}
                disabled={isLoadingCreate || isLoadingUpdate}
              />
              <label htmlFor="showMainBanner" className="ml-2 font-medium text-900">
                Mostrar Banner Principal
              </label>
            </div>
            <div className="flex align-items-center">
              <Checkbox
                id="defaultShowEventMap"
                name="defaultShowEventMap"
                checked={values.defaultShowEventMap}
                onChange={(e) => handleChange({ target: { name: 'defaultShowEventMap', value: e.checked } })}
                disabled={isLoadingCreate || isLoadingUpdate}
              />
              <label htmlFor="defaultShowEventMap" className="ml-2 font-medium text-900">
                Mostrar Mapa de Eventos por Defecto
              </label>
            </div>
          </div>
        </div>

        <div className="field col-12">
          <div className="flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancelar"
              icon="pi pi-times"
              severity="secondary"
              onClick={goBack}
              disabled={isLoadingCreate || isLoadingUpdate}
            />
            <Button
              type="submit"
              label={isEdit ? "Actualizar Tenant" : "Crear Tenant"}
              icon="pi pi-check"
              loading={isLoadingCreate || isLoadingUpdate}
              disabled={!touched || Object.keys(errors).length > 0}
            />
          </div>
        </div>
      </form>
    </div>
  );
};
